import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs';

import { isSameSection, MeetingSection } from '@ninety/platform/models/meetings/meeting-section';
import { Stage } from '@ninety/platform/models/meetings/stage';
import { RealTimeActions, selectDetailIsOpen } from '@ninety/platform/state';

import { MeetingService } from '../../_shared/services/meeting.service';
import { MeetingRealTimeActions, MeetingsPageActions } from '../meetings.actions';
import { MeetingsStateSelectors } from '../meetings.selectors';

/**
 * Autofollow: attendees track the facilitator through the agenda instead of clicking along manually.
 *
 * The facilitator already broadcasts `change-stage` on every move — these effects are the receive
 * side. `MeetingService.executeStageChangeMessage` also consumes that message on the legacy
 * `messageReceived$` path (it syncs elapsed times); both run off the same PubNub delivery.
 */
@Injectable()
export class FollowPresenterEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly meetingService = inject(MeetingService);
  private readonly router = inject(Router);

  /**
   * Tracks where the facilitator is, whatever the flag says — so turning autofollow on mid-meeting
   * doesn't start from a blank slate and paint the avatar on nothing.
   */
  receivePresenterSectionChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === 'change-stage'),
      map(({ message }) => this.resolveSection(message.document as Stage)),
      filter((section): section is MeetingSection => !!section),
      // A copy, not the service's own object: NgRx deep-freezes action payloads in dev, and the 1s
      // timer writes `elapsedTime` on that instance every tick.
      map(section => MeetingRealTimeActions.presenterSectionChanged({ section: { ...section } }))
    )
  );

  private readonly followableMove$ = this.actions$.pipe(
    ofType(MeetingRealTimeActions.presenterSectionChanged),
    concatLatestFrom(() => [
      this.store.select(MeetingsStateSelectors.selectShouldFollowPresenter),
      this.store.select(MeetingsStateSelectors.selectCurrentSection),
      this.store.select(selectDetailIsOpen),
    ]),
    filter(([{ section }, shouldFollow, currentSection]) => shouldFollow && !isSameSection(section, currentSection)),
    map(([{ section }, , , detailIsOpen]) => ({
      section,
      suppress: detailIsOpen || this.hasFocusInFormControl() || !this.isOnAgendaSectionRoute(),
    }))
  );

  autoFollowPresenterSection$ = createEffect(
    () =>
      this.followableMove$.pipe(
        filter(({ suppress }) => !suppress),
        tap(({ section }) => this.navigateToSection(section))
      ),
    { dispatch: false }
  );

  /**
   * Autofollow stands down rather than pulling the page out from under an attendee who is mid-task.
   * It breaks follow rather than just skipping the move, because that is what surfaces the "Sync
   * with facilitator" row — otherwise they drift with no way back.
   */
  breakFollowWhenSuppressed$ = createEffect(() =>
    this.followableMove$.pipe(
      filter(({ suppress }) => suppress),
      map(() => MeetingsPageActions.presenterFollowBroken())
    )
  );

  /**
   * A deliberate section click by an attendee ends autofollow. Safe to key on
   * `meetingSectionChanged` because {@link navigateToSection} never dispatches it — only the sidenav
   * click and skip handlers do.
   */
  breakFollowOnManualNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.meetingSectionChanged),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectIsPresenter)),
      filter(([, isPresenter]) => !isPresenter),
      map(() => MeetingsPageActions.presenterFollowBroken())
    )
  );

  followPresenterClicked$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.followPresenterClicked),
        concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectPresenterSection)),
        map(([, section]) => section),
        filter((section): section is MeetingSection => !!section),
        tap(section => this.navigateToSection(section))
      ),
    { dispatch: false }
  );

  /**
   * Whether the attendee is looking at the agenda at all.
   *
   * Agenda sections live under `/meeting/:id/<meetingType>/…`, but the View tools menu links to
   * `/meeting/:id/<tool>` — both start with `/meeting/`, so a bare prefix check can't tell "watching
   * the agenda" from "stepped out to the Scorecard". Without this, autofollow would yank a reading
   * attendee back into the meeting.
   */
  private isOnAgendaSectionRoute(): boolean {
    const meeting = this.meetingService.currentMeeting;
    if (!meeting) return false;

    const agendaRoot = `/meeting/${meeting._id}/${this.meetingService.getMeetingTypePath(meeting.type)}`;
    return this.router.url.startsWith(agendaRoot);
  }

  /**
   * Stands in for "the user is editing". Named for what it actually measures, because it isn't the
   * same thing — it misses drags and rich-text editors that park focus on a wrapper. There is no
   * app-wide dirty state to read (every feature tracks its own), and focus is the only signal common
   * to all of them, so this reads the DOM. Replace it if that state ever exists.
   */
  private hasFocusInFormControl(): boolean {
    const active = document.activeElement as HTMLElement | null;
    if (!active) return false;

    return active.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName);
  }

  /**
   * Ordinal first, name second. Older clients broadcast without `stageOrdinal`, and section names
   * are not unique across custom agendas — so the fallback is a real compatibility path.
   */
  private resolveSection(stage: Stage): MeetingSection | null {
    const sections = this.meetingService.currentMeeting?.sections;
    if (!sections?.length || !stage) return null;

    const byOrdinal = stage.stageOrdinal != null ? sections.find(s => s.ordinal === stage.stageOrdinal) : null;
    return byOrdinal ?? sections.find(s => s.name === stage.stage) ?? null;
  }

  /**
   * Deliberately does *not* dispatch `meetingSectionChanged`: a follower must neither re-broadcast
   * the move nor emit its analytics event, and dispatching it would immediately trip
   * {@link breakFollowOnManualNavigation$} on the move autofollow just made.
   */
  private navigateToSection(section: MeetingSection): void {
    const meeting = this.meetingService.currentMeeting;
    if (!meeting?.inProgress) return;

    // Index into the service's own array — `startMeetingSectionTimer` subscripts `sections[index]`,
    // and that array is not guaranteed to be ordinal-sorted.
    const index = meeting.sections.findIndex(s => s.ordinal === section.ordinal);
    if (index === -1) return;

    // Hand the service its OWN instance: it ticks `elapsedTime` on these and assigns whatever it is
    // given to `currentSection`, so a copy would detach the two.
    const liveSection = meeting.sections[index];

    if (isSameSection(liveSection, meeting.currentSection)) return;

    this.meetingService.navigate(this.meetingService.getMeetingRoute(liveSection));
    this.meetingService.changeMeetingSection(liveSection, index);
  }
}
